import { useState, useEffect } from "react";
import { useNavigate, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import axios from "axios";
import {
  Shield, Users, Building2, Settings, LogOut, Plus, Trash2, Edit, 
  Check, X, Pause, RefreshCw, LayoutDashboard, Share2, Upload, ExternalLink, FileUp, Download,
  Instagram, Facebook, Youtube, Eye, TrendingUp, BarChart3, Camera, Mail, Ban, Power
} from "lucide-react";

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [landingSettings, setLandingSettings] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [companiesRes, usersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/companies`, authHeader),
        axios.get(`${API}/admin/users`, authHeader),
        axios.get(`${API}/landing-settings`)
      ]);
      setCompanies(companiesRes.data);
      setUsers(usersRes.data);
      setLandingSettings(settingsRes.data);
      
      // Fetch analytics
      try {
        const analyticsRes = await axios.get(`${API}/admin/analytics/overview`, authHeader);
        setAnalytics(analyticsRes.data);
      } catch (e) {
        // Analytics optional — ignore if unavailable
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const stats = {
    totalUsers: users.length,
    totalCompanies: companies.length,
    approvedUsers: users.filter(u => u.status === "approved").length,
    pendingUsers: users.filter(u => u.status === "on_hold").length
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-card border-r p-6 hidden lg:block">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 rounded-xl bg-zinc-900 flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="font-semibold">Super Admin</h2>
            <p className="text-xs text-muted-foreground">System Control</p>
          </div>
        </div>

        <nav className="space-y-2">
          <Button 
            variant={activeTab === "overview" ? "secondary" : "ghost"} 
            className="w-full justify-start"
            onClick={() => setActiveTab("overview")}
            data-testid="nav-overview"
          >
            <LayoutDashboard className="h-4 w-4 mr-2" /> Overview
          </Button>
          <Button 
            variant={activeTab === "companies" ? "secondary" : "ghost"} 
            className="w-full justify-start"
            onClick={() => setActiveTab("companies")}
            data-testid="nav-companies"
          >
            <Building2 className="h-4 w-4 mr-2" /> Companies
          </Button>
          <Button 
            variant={activeTab === "users" ? "secondary" : "ghost"} 
            className="w-full justify-start"
            onClick={() => setActiveTab("users")}
            data-testid="nav-users"
          >
            <Users className="h-4 w-4 mr-2" /> All Users
          </Button>
          <Button 
            variant={activeTab === "landing" ? "secondary" : "ghost"} 
            className="w-full justify-start"
            onClick={() => setActiveTab("landing")}
            data-testid="nav-landing"
          >
            <Settings className="h-4 w-4 mr-2" /> Landing Page
          </Button>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-card border-b p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            <span className="font-semibold">Super Admin</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
          {["overview", "companies", "users", "landing"].map(tab => (
            <Button 
              key={tab}
              variant={activeTab === tab ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 p-6">
        {activeTab === "overview" && <OverviewTab stats={stats} analytics={analytics} />}
        {activeTab === "companies" && (
          <CompaniesTab 
            companies={companies} 
            token={token} 
            onRefresh={fetchData}
          />
        )}
        {activeTab === "users" && (
          <UsersTab 
            users={users}
            companies={companies}
            token={token}
            onRefresh={fetchData}
          />
        )}
        {activeTab === "landing" && (
          <LandingSettingsTab 
            settings={landingSettings}
            token={token}
            onRefresh={fetchData}
          />
        )}
      </main>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ stats, analytics }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
    <h1 className="text-3xl font-bold mb-6">Dashboard Overview</h1>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Users</p>
              <p className="text-3xl font-bold">{stats.totalUsers}</p>
            </div>
            <Users className="h-8 w-8 text-primary opacity-20" />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Companies</p>
              <p className="text-3xl font-bold">{stats.totalCompanies}</p>
            </div>
            <Building2 className="h-8 w-8 text-secondary opacity-20" />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active Users</p>
              <p className="text-3xl font-bold">{stats.approvedUsers}</p>
            </div>
            <Check className="h-8 w-8 text-green-500 opacity-20" />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">On Hold</p>
              <p className="text-3xl font-bold">{stats.pendingUsers}</p>
            </div>
            <Pause className="h-8 w-8 text-yellow-500 opacity-20" />
          </div>
        </CardContent>
      </Card>
    </div>

    {/* Profile Views Analytics */}
    {analytics && (
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <BarChart3 className="h-5 w-5" /> Profile Views Analytics
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Profile Views</p>
                  <p className="text-3xl font-bold">{analytics.total_views}</p>
                </div>
                <Eye className="h-8 w-8 text-blue-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Views (Last 7 Days)</p>
                  <p className="text-3xl font-bold text-green-600">{analytics.views_last_7_days}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card className="sm:col-span-2 lg:col-span-1">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground mb-3">Top Viewed Profiles</p>
              {analytics.top_profiles?.length > 0 ? (
                <div className="space-y-2">
                  {analytics.top_profiles.slice(0, 5).map((profile, idx) => (
                    <div key={profile.user_id || profile.slug || idx} className="flex items-center justify-between text-sm">
                      <span className="truncate flex-1">{profile.name}</span>
                      <span className="font-medium text-muted-foreground">{profile.views} views</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No views yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    )}
  </motion.div>
);

// Companies Tab
const CompaniesTab = ({ companies, token, onRefresh }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form, setForm] = useState({ name: "", admin_email: "" });
  const [editForm, setEditForm] = useState({ name: "" });
  const [loading, setLoading] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const handleCreate = async () => {
    if (!form.name || !form.admin_email) {
      toast.error("Company name and admin email are required");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/companies`, {
        name: form.name,
        admin_email: form.admin_email,
        base_url: window.location.origin,
      }, authHeader);
      toast.success("Company created — an invitation was emailed to the admin");
      setShowDialog(false);
      setForm({ name: "", admin_email: "" });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create company");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setEditForm({ name: company.name });
    setShowEditDialog(true);
  };

  const handleUpdate = async () => {
    if (!editForm.name) {
      toast.error("Company name is required");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API}/companies/${editingCompany.id}`, { name: editForm.name }, authHeader);
      toast.success("Company renamed");
      setShowEditDialog(false);
      setEditingCompany(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rename company");
    } finally {
      setLoading(false);
    }
  };

  const toggleSuspend = async (company) => {
    const next = !(company.is_active === false);
    try {
      await axios.put(`${API}/companies/${company.id}`, { is_active: !next ? true : false }, authHeader);
      toast.success(company.is_active === false ? "Company reactivated" : "Company suspended");
      onRefresh();
    } catch (error) {
      toast.error("Failed to update company");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this company? All its users will be converted to individual users.")) return;
    try {
      await axios.delete(`${API}/companies/${id}`, authHeader);
      toast.success("Company deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete company");
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Companies</h1>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-company-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Company
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Company</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  data-testid="company-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Acme Inc."
                />
              </div>
              <div className="space-y-2">
                <Label>Admin Email</Label>
                <Input
                  data-testid="company-admin-email-input"
                  type="email"
                  value={form.admin_email}
                  onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                  placeholder="admin@company.com"
                />
                <p className="text-xs text-muted-foreground">
                  We&apos;ll email this person an invitation to set up their account and manage the company branding &amp; team.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={loading} data-testid="submit-company-btn">
                {loading ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Rename Company Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Company</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                data-testid="edit-company-name-input"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Acme Inc."
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Branding (logo, colours, theme, welcome message) and team management are handled by the company admin in their own dashboard.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={loading} data-testid="update-company-btn">
              {loading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4">
        {companies.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No companies yet. Create your first company to get started.
            </CardContent>
          </Card>
        ) : (
          companies.map(company => (
            <Card key={company.id} data-testid={`company-card-${company.id}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    {company.logo_url ? (
                      <img 
                        src={company.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${company.logo_url}` : company.logo_url}
                        alt={company.name}
                        className="h-12 w-auto rounded-lg"
                      />
                    ) : (
                      <div 
                        className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: company.primary_color }}
                      >
                        {company.name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold">{company.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {company.is_active === false ? (
                          <Badge variant="outline" className="text-xs border-destructive text-destructive">Suspended</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs border-green-600 text-green-600">Active</Badge>
                        )}
                        {company.admin_email && (
                          <span className="text-xs text-muted-foreground">Admin: {company.admin_email}</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Registration: /join/{company.slug}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEdit(company)}
                      data-testid={`edit-company-${company.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" /> Rename
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => toggleSuspend(company)}
                      data-testid={`suspend-company-${company.id}`}
                    >
                      {company.is_active === false ? (
                        <><Power className="h-4 w-4 mr-1" /> Reactivate</>
                      ) : (
                        <><Ban className="h-4 w-4 mr-1" /> Suspend</>
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(company.id)}
                      data-testid={`delete-company-${company.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </motion.div>
  );
};

// Users Tab
const UsersTab = ({ users, companies, token, onRefresh }) => {
  const [filters, setFilters] = useState({
    company: "all", // "all", "individual", or company id
    role: "all",    // "all", "company_admin", "company_user", "individual"
    status: "all"   // "all", "approved", "on_hold", "disapproved"
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addForm, setAddForm] = useState({ name: "", email: "", password: "", company_id: "", role: "individual" });
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    if (action === "delete" && !window.confirm(`Permanently delete ${selectedIds.length} selected user(s)?`)) return;
    try {
      const res = await axios.post(`${API}/admin/users/bulk-action`, { user_ids: selectedIds, action }, authHeader);
      toast.success(`${action} applied to ${res.data.affected} user(s)`);
      setSelectedIds([]);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk action failed");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { status: newStatus }, authHeader);
      toast.success(`User status updated to ${newStatus}`);
      onRefresh();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Delete this user permanently?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, authHeader);
      toast.success("User deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      profession: user.profession || "",
      profession_details: user.profession_details || "",
      phone: user.phone || "",
      whatsapp: user.whatsapp || "",
      linkedin: user.linkedin || "",
      instagram: user.instagram || "",
      facebook: user.facebook || "",
      twitter: user.twitter || "",
      tiktok: user.tiktok || "",
      snapchat: user.snapchat || "",
      youtube: user.youtube || "",
      email_public: user.email_public || "",
      bio: user.bio || "",
      location: user.location || "",
      website: user.website || "",
      status: user.status || "approved",
      role: user.role || "individual",
      company_id: user.company_id || "",
      password: ""
    });
    setShowEditDialog(true);
  };

  const handleUpdateUser = async () => {
    setLoading(true);
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await axios.put(`${API}/admin/users/${editingUser.id}`, payload, authHeader);
      toast.success("User updated successfully");
      setShowEditDialog(false);
      setEditingUser(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/users/${editingUser.id}/photo`,
        formData,
        { 
          headers: { 
            ...authHeader.headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Update local state with new photo URL
      setEditingUser({ ...editingUser, photo_url: response.data.photo_url });
      toast.success("Photo uploaded successfully");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleAddUser = async () => {
    if (!addForm.name || !addForm.email || !addForm.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users`, addForm, authHeader);
      toast.success("User created successfully");
      setShowAddDialog(false);
      setAddForm({ name: "", email: "", password: "", company_id: "", role: "individual" });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const copyProfileLink = (slug) => {
    const url = `${window.location.origin}/profile/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("E-Card link copied!");
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return "Individual";
    const company = companies.find(c => c.id === companyId);
    return company?.name || "Unknown";
  };

  // Filter users based on selected filters
  const filteredUsers = users.filter(user => {
    // Company filter
    if (filters.company === "individual" && user.company_id) return false;
    if (filters.company !== "all" && filters.company !== "individual" && user.company_id !== filters.company) return false;
    
    // Role filter
    if (filters.role !== "all" && user.role !== filters.role) return false;
    
    // Status filter
    if (filters.status !== "all" && user.status !== filters.status) return false;
    
    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.profession?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  // Stats
  const stats = {
    total: users.length,
    individual: users.filter(u => !u.company_id).length,
    companyUsers: users.filter(u => u.company_id).length,
    approved: users.filter(u => u.status === "approved").length,
    onHold: users.filter(u => u.status === "on_hold").length,
    disapproved: users.filter(u => u.status === "disapproved").length
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">All Users</h1>
        <div className="flex gap-2">
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={addForm.name}
                    onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={addForm.email}
                    onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={addForm.password}
                    onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                    placeholder="Initial password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company (Optional)</Label>
                  <Select value={addForm.company_id || "none"} onValueChange={(v) => setAddForm({ ...addForm, company_id: v === "none" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="No Company (Individual)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Company (Individual)</SelectItem>
                      {companies.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={addForm.role} onValueChange={(v) => setAddForm({ ...addForm, role: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="company_user">Company User</SelectItem>
                      <SelectItem value="company_admin">Company Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button onClick={handleAddUser} disabled={loading}>
                  {loading ? "Creating..." : "Create User"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={onRefresh} data-testid="refresh-users-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <Card className="cursor-pointer hover:border-primary/50" onClick={() => setFilters({ ...filters, company: "all", role: "all", status: "all" })}>
          <CardContent className="py-3 px-4">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary/50" onClick={() => setFilters({ ...filters, company: "individual" })}>
          <CardContent className="py-3 px-4">
            <p className="text-xs text-muted-foreground">Individual</p>
            <p className="text-2xl font-bold">{stats.individual}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary/50" onClick={() => setFilters({ ...filters, company: "all", role: "all", status: "all" })}>
          <CardContent className="py-3 px-4">
            <p className="text-xs text-muted-foreground">Company</p>
            <p className="text-2xl font-bold">{stats.companyUsers}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-green-500/50" onClick={() => setFilters({ ...filters, status: "approved" })}>
          <CardContent className="py-3 px-4">
            <p className="text-xs text-green-600">Approved</p>
            <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-yellow-500/50" onClick={() => setFilters({ ...filters, status: "on_hold" })}>
          <CardContent className="py-3 px-4">
            <p className="text-xs text-yellow-600">On Hold</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.onHold}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-red-500/50" onClick={() => setFilters({ ...filters, status: "disapproved" })}>
          <CardContent className="py-3 px-4">
            <p className="text-xs text-red-600">Disapproved</p>
            <p className="text-2xl font-bold text-red-600">{stats.disapproved}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-1">
              <Label className="text-xs">Search</Label>
              <Input
                placeholder="Name, email, profession..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="user-search-input"
              />
            </div>
            
            {/* Company Filter */}
            <div className="space-y-1">
              <Label className="text-xs">Company</Label>
              <Select value={filters.company} onValueChange={(v) => setFilters({ ...filters, company: v })}>
                <SelectTrigger data-testid="filter-company">
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="individual">Individual Only</SelectItem>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Role Filter */}
            <div className="space-y-1">
              <Label className="text-xs">Role</Label>
              <Select value={filters.role} onValueChange={(v) => setFilters({ ...filters, role: v })}>
                <SelectTrigger data-testid="filter-role">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="company_admin">Company Admin</SelectItem>
                  <SelectItem value="company_user">Company User</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="disapproved">Disapproved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Active Filters & Clear */}
          {(filters.company !== "all" || filters.role !== "all" || filters.status !== "all" || searchQuery) && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {filters.company !== "all" && (
                <Badge variant="secondary" className="gap-1">
                  {filters.company === "individual" ? "Individual" : companies.find(c => c.id === filters.company)?.name}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({ ...filters, company: "all" })} />
                </Badge>
              )}
              {filters.role !== "all" && (
                <Badge variant="secondary" className="gap-1">
                  {filters.role.replace("_", " ")}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({ ...filters, role: "all" })} />
                </Badge>
              )}
              {filters.status !== "all" && (
                <Badge variant="secondary" className="gap-1">
                  {filters.status.replace("_", " ")}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setFilters({ ...filters, status: "all" })} />
                </Badge>
              )}
              {searchQuery && (
                <Badge variant="secondary" className="gap-1">
                  &quot;{searchQuery}&quot;
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setSearchQuery("")} />
                </Badge>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { setFilters({ company: "all", role: "all", status: "all" }); setSearchQuery(""); }}
              >
                Clear All
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results count */}
      <p className="text-sm text-muted-foreground mb-4">
        Showing {filteredUsers.length} of {users.length} users
      </p>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              {/* E-Card Link */}
              <div className="p-4 bg-muted rounded-lg">
                <Label className="text-xs text-muted-foreground">E-Card Link</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    readOnly
                    value={`${window.location.origin}/profile/${editingUser.slug}`}
                    className="bg-background"
                  />
                  <Button 
                    variant="outline"
                    onClick={() => copyProfileLink(editingUser.slug)}
                  >
                    <Share2 className="h-4 w-4 mr-1" /> Copy
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open(`/profile/${editingUser.slug}`, "_blank")}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Profile Photo */}
              <div className="p-4 bg-muted rounded-lg">
                <Label className="text-xs text-muted-foreground mb-3 block">Profile Photo</Label>
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20 border-2 border-background shadow">
                    <AvatarImage 
                      src={editingUser.photo_url ? (editingUser.photo_url.startsWith("/") ? `${API.replace('/api', '')}${editingUser.photo_url}` : editingUser.photo_url) : undefined}
                    />
                    <AvatarFallback className="text-2xl bg-primary/10">{editingUser.name?.charAt(0) || "?"}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      disabled={uploadingPhoto}
                      onClick={() => document.getElementById(`photo-upload-${editingUser.id}`).click()}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      {uploadingPhoto ? "Uploading..." : "Upload Photo"}
                    </Button>
                    <input
                      id={`photo-upload-${editingUser.id}`}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoUpload}
                    />
                    <p className="text-xs text-muted-foreground">JPG, PNG or WebP. Max 5MB.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Profession</Label>
                  <Input
                    value={editForm.profession}
                    onChange={(e) => setEditForm({ ...editForm, profession: e.target.value })}
                    placeholder="e.g., Software Engineer"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Profession Details</Label>
                  <Input
                    value={editForm.profession_details}
                    onChange={(e) => setEditForm({ ...editForm, profession_details: e.target.value })}
                    placeholder="e.g., Specializing in React"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div className="space-y-2">
                  <Label>WhatsApp</Label>
                  <Input
                    value={editForm.whatsapp}
                    onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })}
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>LinkedIn URL</Label>
                  <Input
                    value={editForm.linkedin}
                    onChange={(e) => setEditForm({ ...editForm, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={editForm.website}
                    onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Social Media Links */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium mb-3 block">Social Media Links</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Instagram</Label>
                    <Input
                      value={editForm.instagram}
                      onChange={(e) => setEditForm({ ...editForm, instagram: e.target.value })}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Facebook</Label>
                    <Input
                      value={editForm.facebook}
                      onChange={(e) => setEditForm({ ...editForm, facebook: e.target.value })}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">X (Twitter)</Label>
                    <Input
                      value={editForm.twitter}
                      onChange={(e) => setEditForm({ ...editForm, twitter: e.target.value })}
                      placeholder="https://x.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">TikTok</Label>
                    <Input
                      value={editForm.tiktok}
                      onChange={(e) => setEditForm({ ...editForm, tiktok: e.target.value })}
                      placeholder="https://tiktok.com/@..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Snapchat</Label>
                    <Input
                      value={editForm.snapchat}
                      onChange={(e) => setEditForm({ ...editForm, snapchat: e.target.value })}
                      placeholder="https://snapchat.com/add/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">YouTube</Label>
                    <Input
                      value={editForm.youtube}
                      onChange={(e) => setEditForm({ ...editForm, youtube: e.target.value })}
                      placeholder="https://youtube.com/@..."
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  placeholder="New York, USA"
                />
              </div>

              <div className="space-y-2">
                <Label>Bio</Label>
                <Textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  placeholder="Brief description..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                      <SelectItem value="disapproved">Disapproved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="company_user">Company User</SelectItem>
                      <SelectItem value="company_admin">Company Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Select value={editForm.company_id || "none"} onValueChange={(v) => setEditForm({ ...editForm, company_id: v === "none" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Company (Individual)</SelectItem>
                      {companies.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2 border-t pt-4">
                <Label>Reset Password (optional)</Label>
                <Input
                  data-testid="edit-user-password"
                  type="text"
                  value={editForm.password || ""}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Leave blank to keep current"
                />
                <p className="text-xs text-muted-foreground">Set a new login password for this user (min 6 characters).</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateUser} disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk action bar */}
      {filteredUsers.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3 p-3 rounded-lg border bg-muted/40">
          <label className="flex items-center gap-2 text-sm cursor-pointer font-medium">
            <input
              type="checkbox"
              className="h-4 w-4"
              data-testid="select-all-users"
              checked={selectedIds.length > 0 && filteredUsers.every((u) => selectedIds.includes(u.id))}
              onChange={(e) => setSelectedIds(e.target.checked ? filteredUsers.map((u) => u.id) : [])}
            />
            {selectedIds.length > 0 ? `${selectedIds.length} selected` : "Select all"}
          </label>
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("approve")} data-testid="bulk-approve"><Check className="h-4 w-4 mr-1" />Approve</Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("reactivate")} data-testid="bulk-reactivate"><Power className="h-4 w-4 mr-1" />Reactivate</Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("hold")} data-testid="bulk-hold"><Pause className="h-4 w-4 mr-1" />On Hold</Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("suspend")} data-testid="bulk-suspend"><Ban className="h-4 w-4 mr-1" />Suspend</Button>
              <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleBulkAction("delete")} data-testid="bulk-delete"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-3">
        {filteredUsers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No users match the selected filters.
            </CardContent>
          </Card>
        ) : (
          filteredUsers.map(u => (
            <Card key={u.id} data-testid={`user-card-${u.id}`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      data-testid={`select-user-${u.id}`}
                      checked={selectedIds.includes(u.id)}
                      onChange={() => toggleSelect(u.id)}
                    />
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center font-medium">
                      {u.name?.charAt(0) || "?"}
                    </div>
                    <div>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                      {u.profession && (
                        <p className="text-xs text-muted-foreground">{u.profession}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline">{u.role.replace("_", " ")}</Badge>
                    <Badge 
                      variant="outline"
                      style={u.company_id ? { 
                        borderColor: companies.find(c => c.id === u.company_id)?.primary_color,
                        color: companies.find(c => c.id === u.company_id)?.primary_color
                      } : {}}
                    >
                      {getCompanyName(u.company_id)}
                    </Badge>
                    <Badge className={`status-${u.status}`}>{u.status.replace("_", " ")}</Badge>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEditUser(u)}
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyProfileLink(u.slug)}
                      data-testid={`copy-ecard-${u.id}`}
                    >
                      <Share2 className="h-4 w-4 mr-1" /> E-Card
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleStatusChange(u.id, u.status === "disapproved" ? "approved" : "disapproved")}
                      data-testid={`suspend-user-${u.id}`}
                    >
                      {u.status === "disapproved" ? (
                        <><Power className="h-4 w-4 mr-1" /> Reactivate</>
                      ) : (
                        <><Ban className="h-4 w-4 mr-1" /> Suspend</>
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(u.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </motion.div>
  );
};

// Landing Settings Tab
const LandingSettingsTab = ({ settings, token, onRefresh }) => {
  const [form, setForm] = useState(settings);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    setForm(settings);
  }, [settings]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if PNG
    if (!file.type.includes('png')) {
      toast.error("Only PNG files are allowed for logo");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setForm({ ...form, logo_url: response.data.logo_url });
      toast.success("Logo uploaded");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/landing-settings`, form, authHeader);
      toast.success("Landing page settings updated");
      onRefresh();
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <h1 className="text-3xl font-bold mb-6">Landing Page Settings</h1>
      
      <Card>
        <CardContent className="pt-6 space-y-6">
          {/* Logo Section */}
          <div className="p-4 border rounded-lg space-y-4">
            <Label className="text-base font-medium">Header Logo & Text</Label>
            <div className="flex items-center gap-4">
              {form.logo_url ? (
                <img 
                  src={form.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}` : form.logo_url}
                  alt="Logo" 
                  className="h-12 w-auto rounded-lg border"
                />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                  <Upload className="h-6 w-6 text-muted-foreground" />
                </div>
              )}
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input 
                    type="file" 
                    accept=".png,image/png" 
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                  <Button variant="outline" size="sm" asChild disabled={uploading}>
                    <span>
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? "Uploading..." : "Upload PNG"}
                    </span>
                  </Button>
                </label>
                {form.logo_url && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setForm({ ...form, logo_url: "" })}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Logo Text (displayed next to logo)</Label>
              <Input
                value={form.logo_text || ""}
                onChange={(e) => setForm({ ...form, logo_text: e.target.value })}
                placeholder="tap to share"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Welcome Title</Label>
            <Input
              data-testid="landing-title-input"
              value={form.welcome_title || ""}
              onChange={(e) => setForm({ ...form, welcome_title: e.target.value })}
              placeholder="Your Digital Business Card"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Welcome Subtitle</Label>
            <Textarea
              data-testid="landing-subtitle-input"
              value={form.welcome_subtitle || ""}
              onChange={(e) => setForm({ ...form, welcome_subtitle: e.target.value })}
              placeholder="Create and share your professional profile..."
              rows={3}
            />
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Support WhatsApp</Label>
              <Input
                data-testid="landing-whatsapp-input"
                value={form.support_whatsapp || ""}
                onChange={(e) => setForm({ ...form, support_whatsapp: e.target.value })}
                placeholder="+1234567890"
              />
            </div>
            <div className="space-y-2">
              <Label>Support Email</Label>
              <Input
                data-testid="landing-email-input"
                type="email"
                value={form.support_email || ""}
                onChange={(e) => setForm({ ...form, support_email: e.target.value })}
                placeholder="support@example.com"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Footer Text</Label>
            <Input
              data-testid="landing-footer-input"
              value={form.footer_text || ""}
              onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
              placeholder="© 2026 h75c. All rights reserved."
            />
          </div>
          
          <Button 
            onClick={handleSave} 
            disabled={loading}
            data-testid="save-landing-btn"
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SuperAdminDashboard;
